import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Check, Eye, EyeOff } from 'lucide-react';
import { clsx } from 'clsx';

const schema = z.object({
  adminName:       z.string().min(2, 'Required'),
  adminEmail:      z.string().email('Invalid email'),
  adminPassword:   z.string().min(8, 'Minimum 8 characters'),
  confirmPassword: z.string(),
}).refine(d => d.adminPassword === d.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});
type FormData = z.infer<typeof schema>;

const PLANS = [
  {
    key:   'base',
    name:  'Base',
    price: '₹1,999',
    color: 'border-gray-200',
    badge: '',
    features: ['OPD Queue & Scheduling', 'Patient Registration', 'Basic Billing', '5 Users', '5 GB Storage'],
  },
  {
    key:   'plus',
    name:  'Plus',
    price: '₹3,999',
    color: 'border-orange',
    badge: 'Popular',
    features: ['Everything in Base', 'IPD & Bed Management', 'Pathology (LIS)', 'Radiology (RIS)', 'Pharmacy', 'TPA Billing', 'ABDM / ABHA', '20 Users', '25 GB Storage'],
  },
  {
    key:   'pro',
    name:  'Pro',
    price: '₹6,999',
    color: 'border-navy',
    badge: '',
    features: ['Everything in Plus', 'Operation Theatre', 'Blood Bank', 'OCR Prescriptions', 'Analytics Dashboard', 'API Access', 'Unlimited Users', '100 GB Storage'],
  },
] as const;

type PlanKey = 'base' | 'plus' | 'pro';

interface Props {
  onNext: (plan: PlanKey, adminData: { adminName: string; adminEmail: string; adminPassword: string }) => void;
  onBack: () => void;
}

export default function Step4Modules({ onNext, onBack }: Props) {
  const [plan, setPlan]             = useState<PlanKey>('plus');
  const [showPassword, setShowPwd]  = useState(false);
  const [showConfirm, setShowConf]  = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  function onSubmit(values: FormData) {
    onNext(plan, {
      adminName:     values.adminName,
      adminEmail:    values.adminEmail,
      adminPassword: values.adminPassword,
    });
  }

  const field = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';
  const lbl   = 'block text-sm font-medium text-navy mb-1.5';
  const errC  = 'text-clinical-danger text-xs mt-1';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Plan cards */}
      <div>
        <h3 className="text-sm font-semibold text-navy mb-3">Choose your plan</h3>
        <div className="grid grid-cols-3 gap-3">
          {PLANS.map(p => (
            <button type="button" key={p.key}
              onClick={() => setPlan(p.key)}
              className={clsx(
                'relative rounded-xl border-2 p-4 text-left transition-all',
                plan === p.key ? p.color + ' bg-orange/5' : 'border-gray-200 hover:border-gray-300'
              )}>
              {p.badge && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2
                                 bg-orange text-navy text-2xs font-bold px-2 py-0.5 rounded-full">
                  {p.badge}
                </span>
              )}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-navy">{p.name}</span>
                {plan === p.key && <Check size={14} className="text-orange"/>}
              </div>
              <div className="text-lg font-bold text-navy">{p.price}<span className="text-xs font-normal text-gray-400">/mo</span></div>
              <ul className="mt-3 space-y-1">
                {p.features.map(f => (
                  <li key={f} className="flex items-start gap-1.5 text-xs text-gray-500">
                    <Check size={11} className="text-clinical-success mt-0.5 flex-shrink-0"/>
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>
      </div>

      {/* Admin account */}
      <div>
        <h3 className="text-sm font-semibold text-navy mb-3">Create your Super Admin account</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Full Name *</label>
              <input {...register('adminName')} placeholder="Dr. Ramesh Kumar" className={field}/>
              {errors.adminName && <p className={errC}>{errors.adminName.message}</p>}
            </div>
            <div>
              <label className={lbl}>Email *</label>
              <input {...register('adminEmail')} type="email" placeholder="admin@hospital.in" className={field}/>
              {errors.adminEmail && <p className={errC}>{errors.adminEmail.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Password *</label>
              <div className="relative">
                <input {...register('adminPassword')} type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters" className={`${field} pr-10`}/>
                <button type="button" onClick={() => setShowPwd(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
              </div>
              {errors.adminPassword && <p className={errC}>{errors.adminPassword.message}</p>}
            </div>
            <div>
              <label className={lbl}>Confirm Password *</label>
              <div className="relative">
                <input {...register('confirmPassword')} type={showConfirm ? 'text' : 'password'}
                  placeholder="Re-enter password" className={`${field} pr-10`}/>
                <button type="button" onClick={() => setShowConf(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showConfirm ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
              </div>
              {errors.confirmPassword && <p className={errC}>{errors.confirmPassword.message}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button type="button" onClick={onBack}
          className="flex-1 py-3 rounded-lg border border-gray-200 text-navy text-sm font-medium hover:bg-gray-50 transition-colors">
          Back
        </button>
        <button type="submit"
          className="flex-1 py-3 rounded-lg bg-orange text-navy font-semibold text-sm hover:bg-orange-dark transition-colors">
          Review & Go Live →
        </button>
      </div>
    </form>
  );
}
